import type { ConversationSummaryDto, MowcaDto } from "./api.js";
import { odmien } from "./polski.js";

/**
 * Uczestnicy rozmowy do karty "Uczestnicy": mowcy sesji (z id, do zmiany nazwy) polaczeni z ich
 * miarami z podsumowania rozmowy (po nazwie, bo podsumowanie zna tylko nazwy).
 */

type MetrykiMowcy = ConversationSummaryDto["speakers"][number];

export interface Uczestnik {
  klucz: string;
  /** displayName ?? label. */
  nazwa: string;
  /** Mowca z bazy. null tylko dla miary bez mowcy (starszy ksztalt odpowiedzi). */
  mowca: MowcaDto | null;
  metryki: MetrykiMowcy | null;
}

export function nazwaMowcy(m: { label: string; displayName: string | null }): string {
  return m.displayName ?? m.label;
}

/**
 * Mowcy sesji w kolejnosci z serwera, kazdy z miarami, jesli je ma (mowca bez wypowiedzi ich nie ma).
 * Miary, ktorych nie da sie przypisac zadnemu mowcy, doklejamy na koncu: nie znikaja po cichu.
 */
export function zlozUczestnikow(
  mowcy: readonly MowcaDto[],
  metryki: ConversationSummaryDto["speakers"],
): Uczestnik[] {
  const wgNazwy = new Map(metryki.map((s) => [s.speaker, s]));
  const wynik: Uczestnik[] = mowcy.map((m) => ({
    klucz: m.id,
    nazwa: nazwaMowcy(m),
    mowca: m,
    metryki: wgNazwy.get(nazwaMowcy(m)) ?? null,
  }));

  const uzyte = new Set(wynik.map((u) => u.nazwa));
  for (const s of metryki) {
    if (!uzyte.has(s.speaker)) wynik.push({ klucz: `miara-${s.speaker}`, nazwa: s.speaker, mowca: null, metryki: s });
  }
  return wynik;
}

/** "12,3" - polski przecinek dziesietny, bez zbednego ".0". */
export function liczbaPoPolsku(n: number): string {
  return String(Math.round(n * 10) / 10).replace(".", ",");
}

export interface MiaryUczestnika {
  /** "Udział w czasie" albo "Udział w słowach". */
  etykietaUdzialu: string;
  udzialProcent: string;
  /** Druga linia pod udzialem ("łącznie 42 s mówienia", "łącznie 120 słów"). Null, gdy nie ma z czego. */
  udzialDruga: string | null;
  tury: string;
  /** Srednia tura: wartosc i jednostka; wartosc null, gdy jej nie ma (np. brak danych w czasie). */
  sredniaTura: { wartosc: string | null; jednostka: string };
}

/** Miary jednego uczestnika zaleznie od podstawy podsumowania: czas (nagranie) albo slowa (tekst). */
export function miaryUczestnika(
  m: MetrykiMowcy,
  podstawa: ConversationSummaryDto["basis"],
): MiaryUczestnika {
  const udzialProcent = String(Math.round(m.share * 100));

  if (podstawa === "words") {
    const slowa = m.words ?? null;
    // Jednostka odmienia sie po liczbie, ktora faktycznie widac na ekranie (zaokraglonej do 0,1,
    // jak w liczbaPoPolsku): "1 slowo", "12 slow", ale "17,5 slowa" - przy ulamku dopelniacz liczby pojedynczej.
    const jednostkaSredniej =
      m.avgTurnWords === undefined ? "słów" : odmien(Math.round(m.avgTurnWords * 10) / 10, "słowo", "słowa", "słów");
    return {
      etykietaUdzialu: "Udział w słowach",
      udzialProcent,
      udzialDruga: slowa === null ? null : `łącznie ${slowa} ${odmien(slowa, "słowo", "słowa", "słów")}`,
      tury: String(m.turns),
      sredniaTura: {
        wartosc: m.avgTurnWords === undefined ? null : liczbaPoPolsku(m.avgTurnWords),
        jednostka: jednostkaSredniej,
      },
    };
  }

  return {
    etykietaUdzialu: "Udział w czasie",
    udzialProcent,
    udzialDruga: m.talkSec === null ? null : `łącznie ${Math.round(m.talkSec)} s mówienia`,
    tury: String(m.turns),
    sredniaTura: {
      wartosc: m.avgTurnSec === null ? null : String(m.avgTurnSec).replace(".", ","),
      jednostka: "s",
    },
  };
}
